import { Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';

import type { StatusActivityAction } from '@shared/patient-intake-status';

import { TimelineItem } from './HistoryDialog.types.ts';

const throwNever = (value: never): never => {
  throw new Error(`Unhandled action: ${value}`);
};

const actionToLabel = ({ item }: { item: TimelineItem<StatusActivityAction> }) => {
  const { action } = item;

  switch (action) {
    case 'UPDATE_STATUS':
      return 'Status updated';

    case 'UPDATE_NOTE':
      return 'Note updated';

    case 'CREATE_STEP':
      return 'Step created';

    case 'DELETE_STEP':
      return 'Step deleted';

    case 'TOGGLE_DISABLED': {
      const { isDisabled } = item.metadata;

      return isDisabled ? 'Step disabled' : 'Step enabled';
    }

    default:
      return throwNever(action);
  }
};

const actionToColor = ({ item }: { item: TimelineItem<StatusActivityAction> }) => {
  const { action } = item;

  switch (action) {
    case 'UPDATE_STATUS':
      return 'primary';

    case 'UPDATE_NOTE':
      return 'secondary';

    case 'CREATE_STEP':
      return 'success';

    case 'DELETE_STEP':
      return 'error';

    case 'TOGGLE_DISABLED': {
      const { isDisabled } = item.metadata;

      return isDisabled ? 'warning' : 'success';
    }
    default:
      return throwNever(action);
  }
};

type ActionToIconParams = {
  item: TimelineItem<StatusActivityAction>;
};

const actionToIcon = ({ item }: ActionToIconParams) => {
  const { action } = item;

  switch (action) {
    case 'UPDATE_STATUS':
      return EditIcon;

    case 'UPDATE_NOTE':
      return EditNoteIcon;

    case 'CREATE_STEP':
      return AddIcon;

    case 'DELETE_STEP':
      return DeleteIcon;

    case 'TOGGLE_DISABLED': {
      const { isDisabled } = item.metadata;

      return isDisabled ? ToggleOffIcon : ToggleOnIcon;
    }

    default:
      return throwNever(action);
  }
};

type ActionChipProps = {
  item: TimelineItem<StatusActivityAction>;
};

export const ActionChip: React.FC<ActionChipProps> = ({ item }) => {
  const label = actionToLabel({ item });
  const color = actionToColor({ item });
  const Icon = actionToIcon({ item });

  return (
    <Chip
      label={label}
      size="small"
      color={color}
      variant="outlined"
      sx={{ fontSize: '0.75rem', '& .MuiChip-icon': { fontSize: '0.8rem' }, pl: 0.5 }}
      icon={<Icon fontSize="small" />}
    />
  );
};
